/**
 * ModelsSection Component
 *
 * Compact model configuration with expandable parameters:
 * - Table-like rows for quick scanning
 * - Click chevron to expand/collapse parameters
 * - Minimal visual noise
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Settings2, MessageSquare, Eye, Brain, Search, Sparkles, Palette, RotateCcw, Lock, type LucideIcon } from 'lucide-react';
import { ModelSelectorField } from '@/components/fields/ModelSelectorField';
import { ModelSelect } from '@/components/model-display/ModelSelect';
import { ModelParameters } from '@/components/model-display/ModelParameters';
import { ModelParametersPanel } from '@/components/ModelSelector';
import type { LinkedParameter } from '@/components/ModelSelector/types';
import { MERGED_FIELD_CONFIG as FIELD_CONFIG, getConfigValue, FieldType } from '@/config/fieldConfig';
import { useI18n } from '@/i18n';
import { useModelDefaultsConfig } from '@/features/system-settings/hooks/useSystemConfig';
import type { ModelDefaultsConfig } from '@/types/system';
import { useCatalogModels } from '@/lib/queries/useCatalogModels';
import { modelId as getModelId } from '@/lib/models/catalogAccessors';
import type { ImageCapability } from '@/types';

// Category styling
const CATEGORY_STYLES: Record<string, { icon: LucideIcon; color: string; bg: string; border: string }> = {
  '对话': { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-l-blue-400' },
  '视觉': { icon: Eye, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-l-purple-400' },
  '处理': { icon: Brain, color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-l-rose-400' },
  '检索': { icon: Search, color: 'text-teal-500', bg: 'bg-teal-50', border: 'border-l-teal-400' },
  '画图': { icon: Palette, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-l-emerald-400' },
  '其他': { icon: Sparkles, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-l-amber-400' },
};

interface ModelsSectionProps {
  onChange: (name: string, value: any) => void;
  appContext: any;
  /** Section to highlight (scroll-to + flash animation) */
  highlightSection?: string | null;
  /** Reports whether the image config (size/aspect/quality) is legal for the
   * selected model. A previously-stored illegal value reports `false` so the
   * page can block submit (Task 3.3 / spec I4). */
  onImageConfigValidityChange?: (valid: boolean) => void;
}

// Global models (managed by system settings, read-only in agent config)
const GLOBAL_MODEL_ROWS = [
  { fieldName: 'models.memory.model_id', label: '记忆提取', category: '处理', hint: '摘要总结' },
  { fieldName: 'models.embedding.model_id', label: '向量嵌入', category: '检索', hint: '知识库' },
];

// Sentinel: fallbackHint set to this value means "uses system default, don't show arrow hint"
const FALLBACK_SYSTEM_DEFAULT = 'system_default' as const;

// Per-agent model definitions with optional fallback hints
// hookGate: when set, the row is only visible if this config path is truthy
const MODEL_ROWS = [
  // Conversation
  { fieldName: 'models.chat.model_id', label: '主对话', category: '对话', hint: '复杂分析' },
  { fieldName: 'models.chat.quick_model_id', label: '快速对话', category: '对话', hint: '简单问答', fallbackHint: FALLBACK_SYSTEM_DEFAULT, hookGate: 'hooks.quick_chat.enabled' },
  // Vision — gated by hooks.vision.enabled; quick vision also needs quick_chat
  { fieldName: 'models.vision.model_id', label: '视觉分析', category: '视觉', hint: '主模型不支持视觉时启用', hookGate: 'hooks.vision.enabled' },
  { fieldName: 'models.vision.quick_model_id', label: '快速视觉', category: '视觉', hint: '快速模型不支持视觉时启用', fallbackHint: FALLBACK_SYSTEM_DEFAULT, hookGate: ['hooks.vision.enabled', 'hooks.quick_chat.enabled'] as string[] },
  // Intelligence (per-agent only)
  { fieldName: 'models.classification.model_id', label: '意图分类', category: '处理', hint: '路由判断' },
  { fieldName: 'models.task.model_id', label: '子任务', category: '处理', hint: '压缩等', fallbackHint: FALLBACK_SYSTEM_DEFAULT },
  // Speech — gated by hooks.stt.enabled
  { fieldName: 'models.stt.model_id', label: '语音识别', category: '其他', hint: 'STT', hookGate: 'hooks.stt.enabled' },
  // Reasoning — gated by hooks.reasoning.enabled
  { fieldName: 'models.reasoning.model_id', label: '推理增强', category: '其他', hint: '思考链', hookGate: 'hooks.reasoning.enabled' },
  // Image generation
  { fieldName: 'models.image.model_id', label: '默认画图', category: '画图', hint: '快速画图' },
  { fieldName: 'models.image.quality_model_id', label: '高质量画图', category: '画图', hint: '精细画图' },
];

// Catalog-driven ModelParameters handles these param names; everything else is
// rendered via ModelParametersPanel (legacy schema-only controls like language
// selects or budget_tokens button-groups that are not catalog sampling params).
const SAMPLING_PARAM_NAMES = new Set([
  'temperature', 'top_p', 'max_tokens', 'frequency_penalty', 'presence_penalty', 'reasoning_effort',
]);

function buildLeftoverLinkedParams(
  linkedParameters: string[] | undefined,
  dataSource: any,
  prefix: string,
): LinkedParameter[] {
  if (!linkedParameters?.length) return [];
  const result: LinkedParameter[] = [];
  for (const path of linkedParameters) {
    const paramName = path.replace(`${prefix}.`, '');
    if (SAMPLING_PARAM_NAMES.has(paramName)) continue;
    const paramConfig = FIELD_CONFIG[path];
    if (!paramConfig) continue;
    const rawLabel = typeof paramConfig.label === 'function' ? paramConfig.label() : paramConfig.label;
    const label = typeof rawLabel === 'string' ? rawLabel : '';
    let type: 'number' | 'select' | 'button_group' = 'number';
    if (paramConfig.type === FieldType.SELECT) {
      type = paramConfig.buttonGroup ? 'button_group' : 'select';
    }
    result.push({
      name: path,
      label,
      type,
      value: getConfigValue(dataSource, path),
      options: paramConfig.options,
      min: paramConfig.min,
      max: paramConfig.max,
      step: paramConfig.step,
    });
  }
  return result;
}

// Image generation preference fields. Candidate lists are NOT hardcoded —
// they derive from the image-service capability contract per the selected
// model's sizing.mode (Task 3.3). See ImageSettingsRows below.
const IMAGE_QUALITY_FIELD = 'models.image.quality';
const IMAGE_SIZE_FIELD = 'models.image.image_size';
const IMAGE_ASPECT_FIELD = 'models.image.default_aspect_ratio';

/**
 * Get the default model value for a specific field from ModelDefaultsConfig
 */
function getDefaultForField(fieldName: string, defaults: ModelDefaultsConfig | undefined): string | null {
  if (!defaults) return null;

  // Map fieldName to ModelDefaultsConfig path (per-agent models only)
  const mapping: Record<string, string | null> = {
    'models.chat.model_id': defaults.chat?.model_id || null,
    'models.chat.quick_model_id': defaults.chat?.quick_model_id || null,
    'models.vision.model_id': defaults.vision?.model_id || null,
    'models.vision.quick_model_id': defaults.vision?.quick_model_id || null,
    'models.classification.model_id': defaults.classification?.model_id || null,
    'models.task.model_id': defaults.task?.model_id || null,
    'models.stt.model_id': defaults.stt?.model_id || null,
    'models.reasoning.model_id': defaults.reasoning?.model_id || null,
    'models.image.model_id': defaults.image?.model_id || null,
    'models.image.quality_model_id': defaults.image?.quality_model_id || null,
  };

  return mapping[fieldName] ?? null;
}

interface ModelRowProps {
  fieldName: string;
  label: string;
  hint: string;
  fallbackHint?: string;
  category: string;
  onChange: (name: string, value: any) => void;
  appContext: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  defaultValue: string | null;
}


const ModelRow: React.FC<ModelRowProps> = ({
  fieldName,
  label,
  hint,
  fallbackHint,
  category,
  onChange,
  appContext,
  isExpanded,
  onToggleExpand,
  defaultValue,
}) => {
  const { language } = useI18n();
  const rawFieldConfig = FIELD_CONFIG[fieldName];

  // Override emptyLabel to show the actual system default model name
  const fieldConfig = useMemo(() => {
    if (!rawFieldConfig || !defaultValue || !rawFieldConfig.allowEmpty) return rawFieldConfig;
    const label = language === 'zh'
      ? `使用系统默认 (${defaultValue})`
      : `Use system default (${defaultValue})`;
    return {
      ...rawFieldConfig,
      emptyLabel: label,
    };
  }, [rawFieldConfig, defaultValue, language]);

  const value = getConfigValue(appContext, fieldName) as string | null;
  const hasLinkedParams = (fieldConfig?.linkedParameters && fieldConfig.linkedParameters.length > 0) ?? false;
  const categoryStyle = CATEGORY_STYLES[category] || CATEGORY_STYLES['其他'];

  // Check if current value differs from default
  const canApplyDefault = defaultValue && value !== defaultValue;

  // D5: resolve the selected model against the text catalog
  const { data: textModels } = useCatalogModels('text');

  const selectedModel = useMemo(() => {
    if (!hasLinkedParams || !value) return undefined;
    return textModels?.find((m) => getModelId(m) === value);
  }, [hasLinkedParams, value, textModels]);

  // Build param values keyed by param name (e.g. "temperature", not full path)
  const paramValues = useMemo<Record<string, number | boolean | string>>(() => {
    if (!selectedModel || !fieldConfig?.linkedParameters) return {};
    const prefix = fieldName.replace('.model_id', '');
    const vals: Record<string, number | boolean | string> = {};
    for (const path of fieldConfig.linkedParameters) {
      const pName = path.replace(`${prefix}.`, '');
      const v = getConfigValue(appContext, path);
      if (v !== null && v !== undefined) {
        vals[pName] = v as number | boolean | string;
      }
    }
    return vals;
  }, [selectedModel, fieldConfig, fieldName, appContext]);

  // Non-sampling linked params (e.g. stt.language, reasoning.budget_tokens) that are
  // NOT catalog sampling params — rendered via the legacy ModelParametersPanel.
  const leftoverLinkedParams = useMemo(
    () => buildLeftoverLinkedParams(
      fieldConfig?.linkedParameters,
      appContext,
      fieldName.replace('.model_id', ''),
    ),
    [fieldConfig?.linkedParameters, appContext, fieldName],
  );

  // Map param name → full config path on change
  const handleParamChange = useCallback(
    (paramName: string, paramValue: number | boolean | string | null) => {
      onChange(`${fieldName.replace('.model_id', '')}.${paramName}`, paramValue);
    },
    [fieldName, onChange],
  );

  if (!fieldConfig) return null;

  const handleValueChange = (newValue: string | null) => {
    onChange(fieldName, newValue);
  };

  const handleFormDataChange = (path: string, paramValue: unknown) => {
    onChange(path, paramValue);
  };

  const handleApplyDefault = () => {
    if (defaultValue) {
      onChange(fieldName, defaultValue);
    }
  };

  return (
    <div className={`border-b border-slate-100 last:border-b-0 border-l-2 ${categoryStyle.border}`}>
      {/* Main row */}
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50/50 transition-colors">
        {/* Apply default button */}
        <button
          type="button"
          onClick={canApplyDefault ? handleApplyDefault : undefined}
          disabled={!canApplyDefault}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
            canApplyDefault
              ? 'text-blue-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer'
              : 'text-slate-200 cursor-default'
          }`}
          title={canApplyDefault ? `应用首选: ${defaultValue}` : (defaultValue ? '已是首选模型' : '无首选配置')}
        >
          <RotateCcw className="w-3 h-3" />
        </button>

        {/* Expand toggle (only if has params) */}
        <button
          type="button"
          onClick={hasLinkedParams ? onToggleExpand : undefined}
          className={`w-5 h-5 flex items-center justify-center rounded ${
            hasLinkedParams
              ? 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              : 'text-transparent cursor-default'
          }`}
        >
          {hasLinkedParams && (
            isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Label */}
        <div className="w-20 shrink-0">
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>

        {/* Hint */}
        <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{hint}</span>

        {/* Model Selector */}
        <div className="flex-1 min-w-0">
          <ModelSelectorField
            name={fieldName}
            schema={fieldConfig}
            value={value}
            onChange={handleValueChange}
            appContext={appContext}
            formData={appContext}
            onFormDataChange={handleFormDataChange}
            compact
          />
        </div>

        {/* Fallback hint */}
        {fallbackHint && fallbackHint !== FALLBACK_SYSTEM_DEFAULT && !value && (
          <span className="text-[10px] text-slate-400 shrink-0 hidden md:block">
            → {fallbackHint}
          </span>
        )}
      </div>

      {/* Expanded parameters — catalog sampling params + leftover schema-only controls */}
      {isExpanded && hasLinkedParams && (selectedModel || leftoverLinkedParams.length > 0) && (
        <div className="pl-10 pr-4 pb-2 bg-slate-50/50">
          {selectedModel && (
            <ModelParameters
              model={selectedModel}
              values={paramValues}
              onChange={handleParamChange}
              parameterNames={fieldConfig.linkedParameters?.map((path) => path.split('.').pop()!)}
            />
          )}
          {leftoverLinkedParams.length > 0 && (
            <ModelParametersPanel
              parameters={leftoverLinkedParams}
              onParameterChange={handleFormDataChange}
              compact
            />
          )}
        </div>
      )}
    </div>
  );
};

// Image model slots use the unified contract-driven picker (shared with the
// Playground & Workflow editor) instead of the generic chat ModelSelectorField,
// so only valid image-service models are selectable.
const IMAGE_MODEL_FIELDS = new Set(['models.image.model_id', 'models.image.quality_model_id']);

/**
 * Image model selection row. Uses `ModelSelect` (catalog-driven) so only valid
 * image models are selectable, not chat-tier ids.
 */
const ImageModelRow: React.FC<{
  fieldName: string;
  label: string;
  hint: string;
  category: string;
  onChange: (name: string, value: any) => void;
  appContext: any;
  defaultValue: string | null;
}> = ({ fieldName, label, hint, category, onChange, appContext, defaultValue }) => {
  const { language } = useI18n();

  const value = (getConfigValue(appContext, fieldName) as string | null) ?? '';
  const categoryStyle = CATEGORY_STYLES[category] || CATEGORY_STYLES['其他'];
  const canApplyDefault = !!defaultValue && value !== defaultValue;

  const emptyLabel = defaultValue
    ? (language === 'zh' ? `使用系统默认 (${defaultValue})` : `Use system default (${defaultValue})`)
    : (language === 'zh' ? '使用系统默认' : 'Use system default');

  return (
    <div className={`border-b border-slate-100 last:border-b-0 border-l-2 ${categoryStyle.border}`}>
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50/50 transition-colors">
        <button
          type="button"
          onClick={canApplyDefault ? () => onChange(fieldName, defaultValue) : undefined}
          disabled={!canApplyDefault}
          className={`w-5 h-5 flex items-center justify-center rounded transition-colors ${
            canApplyDefault
              ? 'text-blue-400 hover:text-blue-600 hover:bg-blue-50 cursor-pointer'
              : 'text-slate-200 cursor-default'
          }`}
          title={canApplyDefault ? `应用首选: ${defaultValue}` : (defaultValue ? '已是首选模型' : '无首选配置')}
        >
          <RotateCcw className="w-3 h-3" />
        </button>
        <div className="w-5 shrink-0" />
        <div className="w-20 shrink-0">
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{hint}</span>
        <div className="flex-1 min-w-0">
          <ModelSelect
            value={value}
            onChange={(modelId) => onChange(fieldName, modelId || null)}
            modality="image"
            operationFilter="generate"
            variant="modal"
            allowEmpty
            emptyLabel={emptyLabel}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Compact contract-driven select row for an image preference field.
 * Candidate `options` are passed in (derived from the capability contract).
 * A stored value not in `options` is shown as an explicit error and kept
 * selectable so the operator can see/fix it (it also blocks submit upstream).
 */
const ImageOptionRow: React.FC<{
  fieldName: string;
  label: string;
  hint: string;
  value: string;
  options: string[];
  invalid: boolean;
  onChange: (name: string, value: any) => void;
}> = ({ fieldName, label, hint, value, options, invalid, onChange }) => {
  const categoryStyle = CATEGORY_STYLES['画图'];
  const showStaleValue = invalid && value !== '';

  return (
    <div className={`border-b border-slate-100 last:border-b-0 border-l-2 ${categoryStyle.border}`}>
      <div className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50/50 transition-colors">
        <div className="w-5 shrink-0" />
        <div className="w-5 shrink-0" />
        <div className="w-20 shrink-0">
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{hint}</span>
        <div className="flex-1 min-w-0">
          <select
            value={String(value)}
            onChange={(e) => onChange(fieldName, e.target.value || null)}
            className={`w-full text-sm border rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 ${
              invalid
                ? 'border-red-400 text-red-600 focus:ring-red-400 focus:border-red-400'
                : 'border-slate-200 text-slate-700 focus:ring-blue-400 focus:border-blue-400'
            }`}
          >
            {showStaleValue && (
              <option value={value}>{`${value} （不适用当前模型）`}</option>
            )}
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {invalid && (
            <p className="mt-1 text-[11px] text-red-500">
              当前模型不支持该取值，请重新选择
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Check if a "WxH" size string satisfies image-service constraints.
 * Used for constrained_size models: a stored value that passes constraints is
 * treated as valid even when absent from the sizes list (e.g. a custom dim).
 */
function satisfiesConstraints(
  size: string,
  constraints: Record<string, unknown> | null | undefined,
): boolean {
  if (!constraints) return false;
  if (size.toLowerCase() === 'auto') return true;
  const parts = size.split('x');
  if (parts.length !== 2) return false;
  const w = parseInt(parts[0], 10);
  const h = parseInt(parts[1], 10);
  if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return false;
  const long = Math.max(w, h);
  const short = Math.min(w, h);
  if (constraints.multiple_of != null) {
    const m = constraints.multiple_of as number;
    if (w % m !== 0 || h % m !== 0) return false;
  }
  if (constraints.min_side != null && short < (constraints.min_side as number)) return false;
  if (constraints.max_side != null && long > (constraints.max_side as number)) return false;
  if (constraints.max_ratio != null && short > 0 && long / short > (constraints.max_ratio as number)) return false;
  if (constraints.long_edge_max != null && long > (constraints.long_edge_max as number)) return false;
  return true;
}

/**
 * Contract-driven image preference rows. Reads the selected image model, derives
 * legal size/aspect/quality candidates from the capability contract, and links
 * the controls by sizing.mode:
 *   - aspect_ratio mode → show aspect dropdown only (no size control)
 *   - discrete/constrained size mode → show size dropdown only (no aspect control)
 * Quality dropdown is shown only when the model exposes quality options.
 * Reports validity of the stored selection upstream (blocks submit on illegal).
 */
const ImageSettingsRows: React.FC<{
  appContext: any;
  selectedModelId: string | null;
  onChange: (name: string, value: any) => void;
  onValidityChange?: (valid: boolean) => void;
}> = ({ appContext, selectedModelId, onChange, onValidityChange }) => {
  const { data: imageModels, isLoading } = useCatalogModels('image');

  const imageCap = useMemo((): ImageCapability | undefined => {
    if (!selectedModelId || !imageModels) return undefined;
    const m = imageModels.find((model) => getModelId(model) === selectedModelId);
    if (!m || m.capability.modality !== 'image') return undefined;
    return m.capability as ImageCapability;
  }, [imageModels, selectedModelId]);

  const sizeValue = (getConfigValue(appContext, IMAGE_SIZE_FIELD) as string | null) ?? '';
  const aspectValue = (getConfigValue(appContext, IMAGE_ASPECT_FIELD) as string | null) ?? '';
  const qualityValue = (getConfigValue(appContext, IMAGE_QUALITY_FIELD) as string | null) ?? '';

  const derived = useMemo(() => {
    if (!imageCap) {
      return { mode: undefined, sizes: [] as string[], aspects: [] as string[], qualities: [] as string[], errors: [] as string[] };
    }
    const { sizing } = imageCap;
    const mode = sizing.mode;
    const sizes =
      sizing.mode === 'discrete_size' ? sizing.sizes :
      sizing.mode === 'constrained_size' ? sizing.sizes : [];
    const constraints = sizing.mode === 'constrained_size' ? sizing.constraints : undefined;
    const aspects = sizing.mode === 'aspect_ratio' ? sizing.aspect_ratios : [];
    const qualities = imageCap.quality_options ?? [];

    const errors: string[] = [];
    if (mode === 'aspect_ratio') {
      if (aspectValue && aspects.length > 0 && !aspects.includes(aspectValue)) {
        errors.push(`aspect_ratio '${aspectValue}' not in [${aspects.join(', ')}]`);
      }
    } else {
      if (sizeValue && sizes.length > 0) {
        const inList = sizes.includes(sizeValue);
        if (!inList && !satisfiesConstraints(sizeValue, constraints)) {
          errors.push(`size '${sizeValue}' not in [${sizes.join(', ')}]`);
        }
      }
    }
    if (qualityValue && qualities.length > 0 && !qualities.includes(qualityValue)) {
      errors.push(`quality '${qualityValue}' not in [${qualities.join(', ')}]`);
    }

    return { mode, sizes, aspects, qualities, errors };
  }, [imageCap, sizeValue, aspectValue, qualityValue]);

  const isAspectMode = derived.mode === 'aspect_ratio';
  const sizeInvalid =
    !isAspectMode && sizeValue !== '' && derived.errors.some((e) => e.includes('size'));
  const aspectInvalid =
    isAspectMode && aspectValue !== '' && derived.errors.some((e) => e.includes('aspect_ratio'));
  const qualityInvalid =
    qualityValue !== '' && derived.errors.some((e) => e.includes('quality'));

  // Report validity upstream. Only report once capabilities are loaded so we
  // don't block submit during the catalog fetch.
  useEffect(() => {
    if (!imageCap || !selectedModelId) {
      onValidityChange?.(true);
      return;
    }
    onValidityChange?.(derived.errors.length === 0);
  }, [imageCap, selectedModelId, derived.errors.length, onValidityChange]);

  if (isLoading && selectedModelId) {
    return (
      <div className="px-3 py-2 text-[11px] text-slate-400 border-l-2 border-l-emerald-400">
        加载模型能力中…
      </div>
    );
  }

  return (
    <>
      {isAspectMode ? (
        <ImageOptionRow
          fieldName={IMAGE_ASPECT_FIELD}
          label="默认比例"
          hint="宽高比"
          value={aspectValue}
          options={derived.aspects}
          invalid={aspectInvalid}
          onChange={onChange}
        />
      ) : (
        <ImageOptionRow
          fieldName={IMAGE_SIZE_FIELD}
          label="默认尺寸"
          hint="分辨率"
          value={sizeValue}
          options={derived.sizes}
          invalid={sizeInvalid}
          onChange={onChange}
        />
      )}
      {derived.qualities.length > 0 && (
        <ImageOptionRow
          fieldName={IMAGE_QUALITY_FIELD}
          label="质量"
          hint="画质"
          value={qualityValue}
          options={derived.qualities}
          invalid={qualityInvalid}
          onChange={onChange}
        />
      )}
    </>
  );
};

export const ModelsSection: React.FC<ModelsSectionProps> = ({
  onChange,
  appContext,
  highlightSection = null,
  onImageConfigValidityChange,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { data: modelDefaults } = useModelDefaultsConfig();
  const sectionRef = useRef<HTMLDivElement>(null);

  // The image model whose capabilities drive the size/aspect/quality candidates.
  // Falls back to the system default when the agent has no explicit override.
  const selectedImageModelId =
    (getConfigValue(appContext, 'models.image.model_id') as string | null) ||
    modelDefaults?.image?.model_id ||
    null;

  // Any linkedSection starting with 'models' targets this component
  const isHighlighted = Boolean(highlightSection?.startsWith('models'));

  // Scroll into view when highlighted
  useEffect(() => {
    if (isHighlighted && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [isHighlighted]);

  const toggleExpand = (fieldName: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  // Filter rows by hookGate visibility (supports single string or string[] AND logic)
  const visibleRows = MODEL_ROWS.filter((row) => {
    if (!row.hookGate) return true;
    const gates = Array.isArray(row.hookGate) ? row.hookGate : [row.hookGate];
    return gates.every((gate) => Boolean(getConfigValue(appContext, gate)));
  });

  const groupedRows = visibleRows.reduce((acc, row) => {
    if (!acc[row.category]) acc[row.category] = [];
    acc[row.category].push(row);
    return acc;
  }, {} as Record<string, typeof MODEL_ROWS>);

  return (
    <div
      ref={sectionRef}
      className={`bg-white rounded-lg border shadow-sm transition-all duration-300 ${
        isHighlighted ? 'border-blue-400 ring-2 ring-blue-200 animate-pulse' : 'border-slate-200'
      }`}
    >
      {/* Header */}
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-slate-100 ${isHighlighted ? 'bg-blue-50' : ''}`}>
        <Settings2 className="w-4 h-4 text-slate-400" />
        <h3 className={`text-sm font-medium ${isHighlighted ? 'text-blue-700' : 'text-slate-700'}`}>模型配置</h3>
        <span className="text-xs text-slate-400">· {visibleRows.length + GLOBAL_MODEL_ROWS.length} 项</span>
      </div>

      {/* Global Models (read-only, managed by system settings) */}
      <div className="border-b border-slate-200 bg-slate-50/80">
        <div className="px-4 py-1.5 flex items-center gap-2">
          <Lock className="w-3 h-3 text-amber-500" />
          <span className="text-[11px] font-medium text-amber-600">全局模型</span>
          <span className="text-[10px] text-slate-400">· 由系统设置管理</span>
        </div>
        {GLOBAL_MODEL_ROWS.map((row) => {
          const value = modelDefaults
            ? (modelDefaults as any)[row.fieldName.split('.')[1]]?.model_id
            : null;
          return (
            <div key={row.fieldName} className="flex items-center gap-2 py-1.5 px-3 border-b border-slate-100 last:border-b-0 opacity-60">
              <Lock className="w-3 h-3 text-slate-300 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-20 shrink-0">
                <span className="text-sm text-slate-500">{row.label}</span>
              </div>
              <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">{row.hint}</span>
              <div className="flex-1 min-w-0">
                <span className="text-sm text-slate-400 truncate block">{value || '未配置'}</span>
              </div>
            </div>
          );
        })}
        {/* Reranker status — system-level toggle */}
        {(() => {
          const rerankEnabled = modelDefaults?.reranking?.enabled !== false;
          const rerankModel = modelDefaults?.reranking?.model_id;
          return (
            <div className="flex items-center gap-2 py-1.5 px-3 border-b border-slate-100 last:border-b-0 opacity-60">
              <Lock className="w-3 h-3 text-slate-300 shrink-0" />
              <div className="w-5 shrink-0" />
              <div className="w-20 shrink-0">
                <span className="text-sm text-slate-500">重排序</span>
              </div>
              <span className="text-[11px] text-slate-400 w-14 shrink-0 hidden sm:block">相关性</span>
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${rerankEnabled ? 'bg-green-400' : 'bg-slate-300'}`} />
                <span className="text-sm text-slate-400 truncate">
                  {rerankEnabled ? (rerankModel || '未配置模型') : '已关闭'}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Per-Agent Model Rows */}
      <div>
        {Object.entries(groupedRows).map(([category, rows]) => {
          const style = CATEGORY_STYLES[category] || CATEGORY_STYLES['其他'];
          const Icon = style.icon;
          return (
            <div key={category}>
              {/* Category label */}
              <div className={`px-4 py-1.5 ${style.bg} border-b border-slate-100 flex items-center gap-2`}>
                <Icon className={`w-3.5 h-3.5 ${style.color}`} />
                <span className={`text-[11px] font-medium ${style.color}`}>
                  {category}
                </span>
              </div>
              {/* Rows */}
              {rows.map((row) =>
                IMAGE_MODEL_FIELDS.has(row.fieldName) ? (
                  <ImageModelRow
                    key={row.fieldName}
                    fieldName={row.fieldName}
                    label={row.label}
                    hint={row.hint}
                    category={row.category}
                    onChange={onChange}
                    appContext={appContext}
                    defaultValue={getDefaultForField(row.fieldName, modelDefaults)}
                  />
                ) : (
                  <ModelRow
                    key={row.fieldName}
                    {...row}
                    onChange={onChange}
                    appContext={appContext}
                    isExpanded={expandedRows.has(row.fieldName)}
                    onToggleExpand={() => toggleExpand(row.fieldName)}
                    defaultValue={getDefaultForField(row.fieldName, modelDefaults)}
                  />
                )
              )}
              {/* Image preference settings — contract-driven, linked to the
                  selected image model's sizing.mode (Task 3.3) */}
              {category === '画图' && (
                <ImageSettingsRows
                  appContext={appContext}
                  selectedModelId={selectedImageModelId}
                  onChange={onChange}
                  onValidityChange={onImageConfigValidityChange}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ModelsSection;
